/**
 * The sharing screen's arithmetic, as pure functions — Story #5648 build v2, C2.
 *
 * Everything the "Who can see what of mine" figure states about the caller's
 * relationships is DERIVED, not fetched: how many projects an organisation can
 * see, how many folders are hidden from it, how many of the caller's projects
 * are shared with at least one portfolio. Those numbers appear in three places
 * on one screen (the card, the summary strip, the grid), so they are computed
 * once, here, and cannot disagree with each other.
 *
 * Pure and dependency-free on purpose: the page owns the fetching, these
 * functions own the meaning, and the tests exercise them with fixtures shaped
 * exactly like the live API's responses.
 *
 * THE RULE these functions encode (Rob, 6 Sep 2026): being part of a portfolio
 * shows an organisation nothing. Every project it can see is a grant the
 * inventor made, project by project, and can revoke. So a portfolio's project
 * count is read off the GRANTS, never off the relationship.
 */
import type { AggregatorGrant, MySeat, PendingSeat, Project, SeatChange } from '../../types'

/**
 * What an organisation with no name is called on this screen.
 *
 * The routes answer null rather than the raw aggregator id — a UUID is not a
 * name — so the screen needs a word for the case, and it needs to be the SAME
 * word everywhere: card, grid column, summary strip and feed. Measured on dev
 * 6 Sep 2026: two of charlie-sponsored's four portfolios and two grid columns
 * read "49ee2468-2041-706e-554a-fdacd5110cbd", because those two aggregator ids
 * are team members' own accounts with no organisation name on their row.
 */
export const UNNAMED_PORTFOLIO = 'Unnamed portfolio'

/** The same thing in the middle of a sentence, for the change feed. */
export const UNNAMED_PORTFOLIO_INLINE = 'an unnamed portfolio'

/**
 * The organisation's name INSIDE a sentence — the feed's version of
 * organisationName, and deliberately the same answer.
 *
 * Round 3 disambiguated the CARDS ("Unnamed portfolio (Sarah Kim)") and left the
 * feed saying "an unnamed portfolio" for the same organisation, three inches
 * below. One screen, two different words for one thing, is worse than either
 * word on its own: a reader cannot tell whether they are the same portfolio.
 * So when a holder is known the sentence names it exactly as the card does, and
 * only a genuinely anonymous organisation falls back to the vague form.
 */
export function organisationInSentence(
  name: string | null | undefined,
  holder?: string | null,
): string {
  const trimmed = (name ?? '').trim()
  if (trimmed) return trimmed
  const person = (holder ?? '').trim()
  return person ? `${UNNAMED_PORTFOLIO} (${person})` : UNNAMED_PORTFOLIO_INLINE
}

/**
 * The name to print for one organisation — or the honest placeholder.
 *
 * Two guards, not one. The routes now answer null, which is the first. The
 * second is that a name IDENTICAL to the id is not a name either: the grants
 * route (aggregator_grants/handler.py) resolves its own aggregator_name and
 * still returns the raw id for these same accounts, and that route is another
 * lane's. Rather than let a UUID through because it arrived down a different
 * pipe, the screen refuses anything that is just the id repeated back.
 */
export function organisationName(
  name: string | null | undefined,
  aggregatorId: string,
  holder?: string | null,
): string {
  const trimmed = (name ?? '').trim()
  if (trimmed && trimmed !== aggregatorId) return trimmed
  // No organisation name. Two of charlie's four portfolios are in exactly this
  // state on dev, and two cards both reading "Unnamed portfolio" cannot be told
  // apart — which on a screen about who can see your work is the whole point.
  // The account holder's NAME distinguishes them ("Unnamed portfolio (Sarah
  // Kim)"). It is a name, never an email and never a sub; the route that
  // resolves it returns nothing else.
  const person = (holder ?? '').trim()
  return person ? `${UNNAMED_PORTFOLIO} (${person})` : UNNAMED_PORTFOLIO
}

/**
 * "Meridian Ventures'" but "Acme Aggregator's".
 *
 * Round 3: the invitation card printed "Acme Aggregator' portfolio" three
 * times — the figure's example name ends in s, and the s-less form had been
 * copied out of it as if it were the rule.
 */
export function possessive(name: string): string {
  return /s$/i.test(name.trim()) ? `${name}\u2019` : `${name}\u2019s`
}

/** One of the caller's projects, with whatever the grants route said about it. */
export interface ProjectGrants {
  project: Project
  /**
   * `not-owner` is not a failure. /innovateflow/projects lists projects shared
   * WITH the caller alongside their own, and aggregator grants are an
   * owner-only concept, so the grants route answers 403 for a project they do
   * not own. That is the correct answer, and the screen says so in the row
   * rather than raising an alarm.
   */
  status: 'loading' | 'ready' | 'not-owner' | 'error'
  grants: AggregatorGrant[]
}

/** A portfolio the caller is part of, or has been invited to. */
export interface PortfolioRelationship {
  aggregatorId: string
  /** The organisation's display name; falls back to the id when it has none. */
  organisation: string
  state: 'accepted' | 'invited'
  invitedAt: string | null
  acceptedAt: string | null
  extendedVisibility: boolean
  /** Projects this organisation can see — grants the caller made, nothing else. */
  projectsShared: number
  /** Folders hidden from it across those projects. */
  foldersHidden: number
}

// The month names and both date forms now live in src/lib/shortDate.ts — the
// one place a date becomes words in this build. They were duplicated here and
// in WidestViewPolicy, which reached for toLocaleDateString('en-AU', { month:
// 'short' }) instead and printed "4 Sept 2026" where this printed "4 Sep 2026".
// Re-exported so every existing importer of this model is unaffected.
import { dayAndMonth, shortDate } from '../../lib/shortDate'

export { dayAndMonth, shortDate }

/** What one organisation can see across the caller's projects. */
export function reachOf(aggregatorId: string, projects: ProjectGrants[]): {
  projectsShared: number
  foldersHidden: number
} {
  let projectsShared = 0
  let foldersHidden = 0
  for (const p of projects) {
    const grant = p.grants.find((g) => g.aggregator_id === aggregatorId)
    if (!grant) continue
    projectsShared += 1
    foldersHidden += grant.exclusions.length
  }
  return { projectsShared, foldersHidden }
}

/**
 * The caller's portfolio relationships, accepted first then invited.
 *
 * A row the caller has ACCEPTED and one they have only been INVITED to are two
 * different states of the same thing and the figure shows them side by side, so
 * they are one list here. An organisation that appears in both (invited again
 * after a relationship ended) is shown once, as the accepted one, because that
 * is the state that is live.
 */
export function buildPortfolios(
  seats: MySeat[],
  pending: PendingSeat[],
  projects: ProjectGrants[],
): PortfolioRelationship[] {
  const accepted = seats.map((s) => ({
    aggregatorId: s.aggregator_id,
    organisation: organisationName(s.aggregator_name, s.aggregator_id, s.aggregator_holder),
    state: 'accepted' as const,
    invitedAt: s.invited_at ?? null,
    acceptedAt: s.accepted_at ?? s.sponsor_started_at ?? null,
    extendedVisibility: s.extended_visibility,
    ...reachOf(s.aggregator_id, projects),
  }))
  const held = new Set(accepted.map((a) => a.aggregatorId))
  const invited = pending
    .filter((p) => !held.has(p.aggregator_id))
    .map((p) => ({
      aggregatorId: p.aggregator_id,
      organisation: organisationName(p.aggregator_name, p.aggregator_id, p.aggregator_holder),
      state: 'invited' as const,
      invitedAt: p.invited_at ?? null,
      acceptedAt: null,
      // An invitation carries no consent switch of its own — the caller has
      // consented to nothing yet. The figure still shows the row, off, so the
      // asymmetry is stated before they accept rather than after.
      extendedVisibility: false,
      ...reachOf(p.aggregator_id, projects),
    }))
  return [...accepted, ...invited]
}

/**
 * How many of the caller's own projects at least one organisation can see.
 *
 * Counted over the projects the caller OWNS — a project shared with them is not
 * theirs to grant, so including it would make the denominator a number they
 * cannot act on.
 */
export function sharedWithAtLeastOne(projects: ProjectGrants[]): {
  shared: number
  total: number
} {
  const mine = projects.filter((p) => p.status !== 'not-owner')
  return {
    shared: mine.filter((p) => p.grants.length > 0).length,
    total: mine.length,
  }
}

/**
 * Every organisation that appears anywhere on this screen, as the grid's
 * columns: the ones the caller has granted, plus the portfolios they are part
 * of or invited to (so an organisation with no grants yet still gets a column
 * with a "Grant…" in it, which is how the caller gives it one).
 *
 * Keyed by id, never by name: the display name is free text with no uniqueness
 * check, and merging two organisations into one column is the wrong answer on a
 * screen whose whole job is showing exactly who can see what.
 */
export function organisationColumns(
  projects: ProjectGrants[],
  portfolios: PortfolioRelationship[],
): { id: string; name: string; inPortfolio: boolean }[] {
  const names = new Map<string, string>()
  const partOf = new Set<string>()
  // THE PORTFOLIOS GO FIRST, and that ordering is the whole fix. Round 3 read
  // the grants first, so a nameless organisation got the bare "Unnamed
  // portfolio" from its grant row and the card's disambiguated name — built
  // with the account holder — never got a look in. charlie's grid then carried
  // two adjacent columns both headed "Unnamed portfolio ★" for two different
  // organisations the cards above told apart by person. A relationship row is
  // the better-informed source, so it wins; grants only fill in organisations
  // the caller has granted but is not part of.
  for (const rel of portfolios) {
    names.set(rel.aggregatorId, rel.organisation)
    if (rel.state === 'accepted') partOf.add(rel.aggregatorId)
  }
  for (const p of projects) {
    for (const g of p.grants) {
      if (!names.has(g.aggregator_id)) {
        names.set(g.aggregator_id, organisationName(g.aggregator_name, g.aggregator_id))
      }
    }
  }
  return Array.from(names.entries()).map(([id, name]) => ({
    id,
    name,
    inPortfolio: partOf.has(id),
  }))
}

/**
 * One sentence per event in the caller's own change feed, in the sharing
 * vocabulary — no licensing words anywhere, because none of this is licensing.
 *
 * The organisation is named when the route resolved one. When it did not, the
 * line is worded without a name rather than printing a raw id: a UUID in a
 * sentence about who can see your work is worse than a slightly shorter
 * sentence.
 */
export function describeChange(change: SeatChange): string {
  // An unnamed organisation still gets named as one — "an unnamed portfolio
  // invited you" is a sentence a reader can act on; a UUID is not. When the
  // route knows the account holder, the sentence names the organisation exactly
  // as the card above it does ("Unnamed portfolio (Sarah Kim)"), so the two
  // cannot look like two different things.
  const org = organisationInSentence(change.organisation, change.organisation_holder)
  // THE ACTOR IS A PERSON OR AN ORGANISATION, AND THE TARGET IS NEVER THE SAME
  // ONE. Round 3: the feed read "Acme Aggregator switched extended visibility
  // off for Acme Aggregator" — the organisation as both the doer and the thing
  // done to, which reads as nonsense and hides who actually acted. The route
  // deliberately never names the individual on the other side (it collapses
  // every actor to "you" or "sponsor"), so an organisation-initiated change
  // names the organisation ONCE, as the actor, and drops the target.
  const byYou = change.actor === 'you'
  const project = (change.project || '').trim()
  switch (change.event) {
    case 'invited':
      return `${capitalise(org)} invited you to its portfolio.`
    case 'accepted':
      return `you accepted ${possessive(org)} invitation — it showed them nothing until you granted.`
    case 'declined':
      return `you declined ${possessive(org)} invitation.`
    case 'left':
      return `you left ${possessive(org)} portfolio.`
    case 'ended':
      return `${capitalise(org)} ended its relationship with you.`
    case 'suspended':
      return `${capitalise(org)} paused its relationship with you.`
    case 'resumed':
      return `${capitalise(org)} resumed its relationship with you.`
    case 'granted':
      return project
        ? `you granted ${org} a view of ‘${project}’.`
        : `you granted ${org} a view of a project.`
    case 'revoked':
      return byYou
        ? (project
          ? `you revoked ${possessive(org)} view of ‘${project}’.`
          : `you revoked ${possessive(org)} view of a project.`)
        : (project
          ? `${capitalise(org)} gave up its view of ‘${project}’.`
          : `${capitalise(org)} gave up its view of a project.`)
    case 'folder_hidden':
      return project
        ? `you hid a folder on ‘${project}’ from ${org}.`
        : `you hid a folder from ${org}.`
    case 'folder_shown':
      return project
        ? `you un-hid a folder on ‘${project}’ from ${org}.`
        : `you un-hid a folder from ${org}.`
    case 'extended_visibility_enabled':
      return byYou
        ? `you switched extended visibility on for ${org}.`
        : `${capitalise(org)} switched extended visibility on.`
    case 'extended_visibility_disabled':
      return byYou
        ? `you switched extended visibility off for ${org}.`
        : `${capitalise(org)} switched extended visibility off.`
    default:
      // Never a silent drop: an event this screen has no sentence for still
      // appears, saying plainly that it happened, so the feed is never a
      // shorter story than the audit log.
      return byYou
        ? `you changed your sharing with ${org} (${change.event}).`
        : `${capitalise(org)} changed your sharing (${change.event}).`
  }
}

/** "an unnamed portfolio" -> "An unnamed portfolio" at the start of a line. */
function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** The words in the sentence that are bold in the figure. */
export const CHANGE_EMPHASIS: Record<string, string> = {
  invited: 'invited',
  accepted: 'accepted',
  declined: 'declined',
  left: 'left',
  ended: 'ended',
  suspended: 'paused',
  resumed: 'resumed',
  granted: 'granted',
  revoked: 'revoked',
  folder_hidden: 'hid a folder',
  folder_shown: 'un-hid a folder',
  extended_visibility_enabled: 'extended visibility on',
  extended_visibility_disabled: 'extended visibility off',
}

/** One line of the feed: a sentence, when it happened, and how many times. */
export interface ChangeEntry {
  change: SeatChange
  /** Newest occurrence of this exact sentence in this run. */
  at: string
  /** Oldest occurrence, when the run is longer than one. */
  firstAt: string
  count: number
  /**
   * Set when the line is a collapsed CHURN — an action and its undo, repeated.
   * Then this is the sentence to print instead of describeChange(change), and
   * pairEmphasis is the phrase the figure bolds inside it.
   */
  pairText?: string
  pairEmphasis?: string
}

/**
 * An action and the action that undoes it. Only these three: each is one
 * person changing their mind about one thing, so a rapid alternation between
 * them is churn rather than a sequence of separate decisions.
 *
 * accepted/ended is deliberately NOT here even though it alternates the same
 * way. Those are TWO DIFFERENT ACTORS — you accepted, they ended it — and the
 * round-3 review specifically found that leaving the "ended" rows visible
 * between the accepts is what made beta's history legible. Collapsing them
 * would take that back.
 */
const CHURN_PAIRS: [string, string][] = [
  ['granted', 'revoked'],
  ['folder_hidden', 'folder_shown'],
  ['extended_visibility_enabled', 'extended_visibility_disabled'],
]

function churnPairOf(a: string, b: string): [string, string] | null {
  for (const pair of CHURN_PAIRS) {
    if ((pair[0] === a && pair[1] === b) || (pair[0] === b && pair[1] === a)) return pair
  }
  return null
}

/** "21:45" — the time, for a run of identical lines on the same day. */
export function timeOfDay(iso: string | null | undefined): string {
  if (!iso) return ''
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return ''
  return `${String(parsed.getHours()).padStart(2, '0')}:${String(parsed.getMinutes()).padStart(2, '0')}`
}

/**
 * Collapse a run of identical lines into one entry that says how many.
 *
 * Round 3, measured on dev: beta-owner's feed printed "you accepted Acme
 * Aggregator's invitation" THIRTEEN times, every line dated "4 Sep", beside a
 * card reading "Invited · not yet accepted" — so the screen appeared to
 * contradict itself. It did not. Those are thirteen REAL accepts from thirteen
 * accept→end→accept cycles run by the sponsorship test suite between 21:44 and
 * 21:55 on 4 Sep (SPONSORSHIP_ENDED rows sit between them), and the pending
 * invitation on the card is a NEW one sent on 6 Sep. History, not a state bug.
 *
 * The reader could not see that, because thirteen lines with the same date and
 * the same words look like one thing printed thirteen times. So an unbroken run
 * of the same sentence becomes one line carrying its count and its time span —
 * history that reads as history.
 *
 * Only CONSECUTIVE identical lines collapse. A run broken by another event is
 * two separate things that happened, and merging them across the break would
 * be inventing a story the log does not tell.
 */
export function collapseChanges(changes: SeatChange[]): ChangeEntry[] {
  const ordered = changes
    .slice()
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
  const out: ChangeEntry[] = []
  for (const change of ordered) {
    const last = out[out.length - 1]
    if (last && describeChange(last.change) === describeChange(change)) {
      last.count += 1
      last.firstAt = change.at
      continue
    }
    out.push({ change, at: change.at, firstAt: change.at, count: 1 })
  }
  return collapseChurn(out)
}

/**
 * How one churn reads, per pair. Counts are keyed by EVENT, never by the order
 * the run happened to start in — a run can begin on either half, and "12 grants
 * and 12 revocations" has to mean what it says.
 */
const CHURN_WORDING: Record<string, {
  emphasis: string
  sentence: (org: string, project: string, counts: Record<string, number>) => string
}> = {
  granted: {
    emphasis: 'granted and revoked',
    sentence: (org, project, c) =>
      `you granted and revoked ${possessive(org)} view`
      + (project ? ` of \u2018${project}\u2019` : '')
      + ` \u2014 ${c.granted ?? 0} grants and ${c.revoked ?? 0} revocations.`,
  },
  folder_hidden: {
    emphasis: 'hid and un-hid a folder',
    sentence: (org, project, c) =>
      'you hid and un-hid a folder'
      + (project ? ` on \u2018${project}\u2019` : '')
      + ` from ${org} \u2014 ${c.folder_hidden ?? 0} hidden and ${c.folder_shown ?? 0} shown again.`,
  },
  extended_visibility_enabled: {
    emphasis: 'on and off',
    sentence: (org, _project, c) =>
      `you switched extended visibility on and off for ${org}`
      + ` \u2014 ${c.extended_visibility_enabled ?? 0} on and ${c.extended_visibility_disabled ?? 0} off.`,
  },
}

/** The wording for whichever half of the pair a run happens to start on. */
function churnWording(event: string) {
  const pair = CHURN_PAIRS.find((p) => p[0] === event || p[1] === event)
  return pair ? CHURN_WORDING[pair[0]] : undefined
}

/**
 * Collapse an ALTERNATION of an action and its undo into one honest line.
 *
 * Measured on dev 6 Sep 2026 as bob-inventor: 22 of his 28 feed rows were the
 * same two sentences alternating — "you revoked Acme Aggregator's view of
 * 'instant kettle'" / "you granted Acme Aggregator a view of 'instant kettle'",
 * every one dated 4 Sep. The identical-run collapse cannot touch that, because
 * consecutive lines are never identical: they alternate. The card therefore
 * still drowned, and the five other things that happened to bob's sharing were
 * pushed out of sight by one project's churn.
 *
 * It reads as one event because it IS one: somebody exercising grant/revoke
 * twelve times in a minute. So the run becomes a single line that names both
 * counts — "12 grants and 12 revocations" — rather than a rounded "N times"
 * that would have to lie whenever the two counts differ (a run can end on
 * either half).
 *
 * FOUR entries minimum, i.e. two full cycles. A grant followed by a revoke is
 * two decisions and stays two lines; the reader should see that.
 */
export function collapseChurn(entries: ChangeEntry[]): ChangeEntry[] {
  const out: ChangeEntry[] = []
  let i = 0
  while (i < entries.length) {
    const pair = entries[i + 1]
      ? churnPairOf(entries[i].change.event, entries[i + 1].change.event)
      : null
    // The run has to be about ONE organisation and ONE project, or it is two
    // stories that happen to interleave, not a churn.
    const sameSubject = (a: ChangeEntry, b: ChangeEntry) =>
      (a.change.organisation ?? null) === (b.change.organisation ?? null)
      && (a.change.project ?? null) === (b.change.project ?? null)
    let end = i
    if (pair) {
      end = i + 1
      while (
        end + 1 < entries.length
        && entries[end + 1].change.event === entries[end - 1].change.event
        && sameSubject(entries[end + 1], entries[i])
      ) end += 1
    }
    const run = entries.slice(i, end + 1)
    if (pair && run.length >= 4 && run.every((e) => sameSubject(e, run[0]))) {
      const wording = churnWording(run[0].change.event)
      const counts: Record<string, number> = {}
      let total = 0
      for (const e of run) {
        counts[e.change.event] = (counts[e.change.event] ?? 0) + e.count
        total += e.count
      }
      const org = organisationInSentence(run[0].change.organisation, run[0].change.organisation_holder)
      const project = (run[0].change.project || '').trim()
      out.push({
        change: run[0].change,
        at: run[0].at,
        firstAt: run[run.length - 1].firstAt,
        count: total,
        pairText: wording?.sentence(org, project, counts),
        pairEmphasis: wording?.emphasis,
      })
      i = end + 1
      continue
    }
    out.push(entries[i])
    i += 1
  }
  return out
}

/** "between 21:45 and 21:54 on 4 Sep", or across days "between 9 Jun and 4 Sep". */
export function spanLabel(firstAt: string, at: string): string {
  const from = dayAndMonth(firstAt)
  const to = dayAndMonth(at)
  if (!from || !to) return ''
  if (from === to) {
    const t1 = timeOfDay(firstAt)
    const t2 = timeOfDay(at)
    return t1 && t2 && t1 !== t2 ? `between ${t1} and ${t2} on ${to}` : `on ${to}`
  }
  return `between ${from} and ${to}`
}

/** A portfolio the caller used to be part of. */
export interface FormerPortfolio {
  aggregatorId: string
  organisation: string
  /** When it ended, from the caller's own change feed. */
  endedAt: string | null
  /** Whether they left, or the organisation ended it. */
  endedBy: 'you' | 'organisation'
  /** Projects it can STILL see, because a grant outlives the relationship. */
  stillHolds: number
}

/**
 * Portfolios the caller has left, or that ended.
 *
 * There is no route for this and there does not need to be one: the caller's own
 * change feed already records every ending, with the organisation's name, and
 * /sponsorship/my-seats already excludes an ended row. An organisation that
 * appears in the feed with an ending and is NOT in the live list is one they
 * used to be part of.
 *
 * `stillHolds` is the fact the figure exists to state: leaving does not revoke
 * anything. The organisation's structural view stops on its own, and every
 * project grant the inventor made stays until they revoke it — so the count
 * comes from the grants, and the button beside it is the one that changes it.
 */
export function buildLeftPortfolios(
  changes: SeatChange[],
  live: PortfolioRelationship[],
  projects: ProjectGrants[],
): FormerPortfolio[] {
  const liveIds = new Set(live.map((r) => r.aggregatorId))
  const endings = new Map<string, { at: string; by: 'you' | 'organisation'; name: string }>()
  const restarts = new Map<string, string>()

  for (const c of changes) {
    const name = (c.organisation || '').trim()
    // An ending from an organisation with no name cannot become a card: the feed
    // publishes no raw ids, so the only thing left to key on is the name, and two
    // different unnamed portfolios would collapse into one row claiming the
    // grants of both. On a screen about who can see your work, one wrong row is
    // worse than one missing one — and the grid below still shows every grant
    // either of them holds.
    if (!name) continue
    if (c.event === 'left' || c.event === 'ended') {
      const prior = endings.get(name)
      if (!prior || new Date(c.at) > new Date(prior.at)) {
        endings.set(name, { at: c.at, by: c.event === 'left' ? 'you' : 'organisation', name })
      }
    }
    if (c.event === 'accepted') {
      const prior = restarts.get(name)
      if (!prior || new Date(c.at) > new Date(prior)) restarts.set(name, c.at)
    }
  }

  const byName = new Map<string, string>()
  for (const p of projects) {
    for (const g of p.grants) {
      byName.set(organisationName(g.aggregator_name, g.aggregator_id), g.aggregator_id)
    }
  }
  for (const rel of live) byName.set(rel.organisation, rel.aggregatorId)

  const out: FormerPortfolio[] = []
  for (const [name, ending] of endings) {
    // Accepted again after it ended: the relationship is live, not former.
    const restarted = restarts.get(name)
    if (restarted && new Date(restarted) > new Date(ending.at)) continue
    const id = byName.get(name)
    if (id && liveIds.has(id)) continue
    out.push({
      aggregatorId: id ?? name,
      organisation: name,
      endedAt: ending.at,
      endedBy: ending.by,
      stillHolds: id ? reachOf(id, projects).projectsShared : 0,
    })
  }
  return out.sort((a, b) => new Date(b.endedAt ?? 0).getTime() - new Date(a.endedAt ?? 0).getTime())
}
